import React, { useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Avatar,
  Button,
  Checkbox,
  createStyles,
  Divider,
  Group,
  Input,
  Loader,
  NumberInput,
  Select,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconChevronLeft, IconTrash, IconUser } from '@tabler/icons-react';
import AvailableDiscounts from 'components/Discounts/AvailableDiscounts';
import AthleteAutoComplete, {
  AthleteAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/AthleteAutoComplete';
import PaymentMethodSelect from 'components/form-fields/auto-complete-fields/PaymentMethodSelect';
import InvoiceSummary from 'components/invoices/InvoiceDrawer/components/InvoiceSummary';
import MoneyField from 'components/MoneyField';
import StripeTerminalReaderStatusIndicator from 'components/StripeTerminalReader/StripeTerminalReaderStatusIndicator';
import TaxRatePercentageInput from 'components/TaxRatePercentageInput';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useGym from 'hooks/gym/useGym';
import useAthleteUpcomingInvoices from 'hooks/invoice/useAthleteUpcomingInvoices';
import useCreateInvoice from 'hooks/invoice/useCreateInvoice';
import { TapToPayPage, useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useStripeTerminalReaderStatus from 'hooks/stripeTerminalReader/useStripeTerminalReaderStatus';
import { useNavigate } from 'react-router-dom';
import { InvoiceItemWrite, ProductRetrieve } from 'types';
import { NonStripePaymentMethod, TAP_TO_PAY_VALUE } from 'types/enums';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import AddToInvoiceSuccessModal from './AddToInvoiceSuccessModal';
import TapToPayModal from './components/TapToPayModal';

const useStyles = createStyles(theme => ({
  container: {
    width: '50%',
    height: '100%',
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
    background: theme.colors.gray[1],
    [theme.fn.smallerThan('lg')]: {
      width: '100%',
    },
  },
  top: {
    display: 'grid',
    alignItems: 'start',
    gridTemplateColumns: `3fr 2fr 2fr`,
    gridTemplateRows: 'auto',
    gap: theme.spacing.md,
    gridTemplateAreas: `
    'athlete collection date'
    'payment autopay .'
    `,
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: `3fr 2fr`,
      gridTemplateAreas: `
        'athlete .'
        'collection date'
        'payment autopay'
      `,
    },
  },
  athlete: {
    gridArea: 'athlete',
  },
  collection: {
    gridArea: 'collection',
  },
  date: {
    gridArea: 'date',
  },
  payment: {
    gridArea: 'payment',
  },
  autopay: {
    gridArea: 'autopay',
  },
  items: {
    display: 'grid',
    alignItems: 'start',
    gridTemplateColumns: `2fr 7fr 6fr 3fr 6fr 2fr`,
    gridTemplateRows: 'min-content',
    gridTemplateAreas: `'image name pretaxPrice quantity taxRate trash'`,
    columnGap: theme.spacing.md,
    rowGap: theme.spacing.xs,
    whiteSpace: 'nowrap',
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: `2fr 3fr 3fr 4fr 1fr`,
      gridTemplateAreas: `
      'image name name name name'
      'pretaxPrice pretaxPrice quantity taxRate trash'
      `,
    },
  },
  image: {
    gridArea: 'image',
  },
  name: {
    gridArea: 'name',
    overflowX: 'hidden',
  },
  pretaxPrice: {
    gridArea: 'pretaxPrice',
  },
  quantity: {
    gridArea: 'quantity',
  },
  taxRate: {
    gridArea: 'taxRate',
  },
  trash: {
    gridArea: 'trash',
  },
}));

export interface InvoiceItem extends InvoiceItemWrite {
  productId: string;
  productName: string;
  description: string | null;
  type: 'FEES AND MISCELLANEOUS' | 'RETAIL' | 'PACKAGE' | 'CANCELLATION AND NO SHOW FEE';
  isFavorite: boolean;
  image: string | null;
  discount: string | null;
  discountValue?: number;
  discountType?: 'PERCENTAGE' | 'FIXED_AMOUNT';
}

const COLLECTION_OPTIONS = {
  NOW: 'Now',
  FUTURE_DATE: 'Future Date',
  ADD_TO_NEXT_INVOICE: 'Add to next invoice',
} as const;

type ChargeTimeline = (typeof COLLECTION_OPTIONS)[keyof typeof COLLECTION_OPTIONS];

const COLLECTION_OPTIONS_ARRAY: ChargeTimeline[] = [
  COLLECTION_OPTIONS.FUTURE_DATE,
  COLLECTION_OPTIONS.NOW,
  COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE,
];

interface FormValues {
  athlete: AthleteAutoCompleteValue;
  dueDate: Date | null;
  autoPay: 'True' | 'False';
  paymentMethod: string;
  nonStripePaymentMethod: 'Cash or Check' | 'Gift Card' | 'E Transfer' | 'POS Terminal' | null;
  chargeTimeline: ChargeTimeline;
  invoiceItems: InvoiceItem[];
  isTapToPay: boolean;
  isSaveCardForFuturePayments: boolean;
}

const SUBMIT_BUTTON_TEXT: Record<FormValues['chargeTimeline'], string> = {
  [COLLECTION_OPTIONS.NOW]: 'Charge',
  [COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE]: 'Add to Invoice',
  [COLLECTION_OPTIONS.FUTURE_DATE]: 'Create',
} as const;

const today = new Date();
today.setHours(0, 0, 0, 0);
const minDueDate = dayjs().add(1, 'day').toDate();

const getInvoiceItemFromProduct = (item: ProductRetrieve): InvoiceItem => ({
  productId: item.productId,
  invoiceItemId: uuidv4(),
  taxRateValue: (item.taxRate?.rate || 0) * 100,
  itemPrice: (item.price ?? 0) / 100,
  productName: item.name,
  description: item.description,
  type: item.type,
  isFavorite: item.isFavorite,
  image: item.image,
  productPrice: item.productPrices[0],
  quantity: 1,
  discount: null,
});

const getSubmitButtonText = (chargeTimeline: FormValues['chargeTimeline']): string => {
  return SUBMIT_BUTTON_TEXT[chargeTimeline];
};

const ShoppingCart = ({
  isDesktop,
  visible,
  cartItems,
  setCartItems,
  closeCart,
  permission = true,
  productTypeModule,
}: {
  isDesktop: boolean;
  visible: boolean;
  cartItems: ProductRetrieve[];
  setCartItems: React.Dispatch<React.SetStateAction<ProductRetrieve[]>>;
  closeCart: () => void;
  permission?: boolean;
  productTypeModule: string;
}): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const { session } = useAuth();
  const { copy, country, calculateInvoiceSummary } = useLocalization();
  const staffTracking = useStaffTracking();
  // The shopping cart only ever runs from Retail or Fees-and-Misc pages; mobile's
  // 'packages' tab and empty-string fallback both treat as retail for analytics.
  const tapToPayPage: TapToPayPage = productTypeModule === 'fees-and-misc' ? 'misc_and_fees' : 'retails';

  const {
    data: gymData,
    isLoading: isGymDataLoading,
    isError: isGymDataError,
  } = useGym(session?.gym?.gymId);
  const { data: stripeTerminalReaderStatusData } = useStripeTerminalReaderStatus({
    enabled: !!gymData?.data.stripeTerminalReader,
  });

  const createInvoiceMutation = useCreateInvoice();

  const [isTapToPayDrawerOpened, setIsTapToPayDrawerOpened] = useState(false);
  const [successModalOpened, setSuccessModalOpened] = useState(false);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null);

  const form = useForm<FormValues>({
    initialValues: {
      athlete: {
        athleteId: '',
        value: '',
        label: '',
      },
      dueDate: today,
      autoPay: 'False',
      paymentMethod: '',
      nonStripePaymentMethod: null,
      chargeTimeline: COLLECTION_OPTIONS.NOW,
      invoiceItems: [],
      isTapToPay: false,
      isSaveCardForFuturePayments: false,
    },

    validate: {
      athlete: value => (value?.athleteId ? null : 'Athlete is required'),
      autoPay: (value, formValues) =>
        value === 'True' && formValues.nonStripePaymentMethod !== null
          ? 'Autopay cannot be on when payment method is a non-stripe type'
          : null,
      paymentMethod: (value, formValues) => {
        if (formValues.chargeTimeline === COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE) {
          return null;
        }
        if (formValues.isTapToPay) {
          return null;
        }
        if (!value && !formValues.nonStripePaymentMethod) {
          return 'Payment method is required';
        }
      },
    },
  });

  // Check for upcoming invoices when athlete is selected
  const { hasUpcomingInvoices, isLoading: isLoadingUpcomingInvoices } = useAthleteUpcomingInvoices(
    form.values.athlete.athleteId || undefined,
  );

  const { total } = calculateInvoiceSummary(form.values.invoiceItems);

  const availableCollectionOptions = useMemo(() => {
    if (hasUpcomingInvoices && !form.values.isTapToPay) {
      return COLLECTION_OPTIONS_ARRAY;
    }
    return COLLECTION_OPTIONS_ARRAY.filter(option => option !== COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE);
  }, [hasUpcomingInvoices, form.values.isTapToPay]);

  const isChargeButtonDisabled = cartItems.length === 0;

  const handleAthleteChange = (item: AthleteAutoCompleteValue | null): void => {
    const selectedAthlete = {
      athleteId: item ? item.athleteId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('athlete', selectedAthlete);
  };

  const handleChargeTimelineChange = (value: ChargeTimeline): void => {
    form.setFieldValue('chargeTimeline', value);
    form.setFieldValue('dueDate', value === COLLECTION_OPTIONS.NOW ? today : minDueDate);
    if (form.values.isTapToPay && value === COLLECTION_OPTIONS.FUTURE_DATE) {
      form.setFieldValue('isSaveCardForFuturePayments', true);
    }
  };

  const handlePaymentChange = (value: string): void => {
    form.setFieldValue('paymentMethod', value);
    if (value === TAP_TO_PAY_VALUE) {
      form.setFieldValue('isTapToPay', true);
      form.setFieldValue('nonStripePaymentMethod', null);
      form.setFieldValue('autoPay', 'False');
      if (form.values.chargeTimeline === COLLECTION_OPTIONS.FUTURE_DATE) {
        form.setFieldValue('isSaveCardForFuturePayments', true);
      }
      staffTracking.trackTapToPaySelected({
        page: tapToPayPage,
        is_charge_now: form.values.chargeTimeline === COLLECTION_OPTIONS.NOW,
        reader_online: stripeTerminalReaderStatusData?.data.isOnline ?? false,
      });
    } else if (Object.values(NonStripePaymentMethod).includes(value as NonStripePaymentMethod)) {
      form.setFieldValue('isTapToPay', false);
      form.setFieldValue('nonStripePaymentMethod', value as FormValues['nonStripePaymentMethod']);
      form.setFieldValue('autoPay', 'False');
    } else {
      form.setFieldValue('isTapToPay', false);
      form.setFieldValue('nonStripePaymentMethod', null);
    }
  };

  const handleClearCart = (): void => {
    setCartItems([]);
  };

  const handleTrashIcon = (i: number): void => {
    setCartItems(items => items.filter(item => item.productId != form.values.invoiceItems[i].productId));
  };

  const handleSubmit = (formValues: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    const newInvoiceId = uuidv4();
    const isAddToNextInvoice = formValues.chargeTimeline === COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE;

    if (formValues.isTapToPay) {
      if (!stripeTerminalReaderStatusData?.data.isOnline) {
        errorNotification(
          'Terminal reader offline',
          'The card reader is not online. Please check the device and try again.',
        );
        return;
      }
    }

    setSelectedAthleteId(formValues.athlete.athleteId);
    createInvoiceMutation.mutate(
      {
        athlete: formValues.athlete.athleteId,
        autoPay:
          formValues.chargeTimeline == COLLECTION_OPTIONS.FUTURE_DATE && formValues.autoPay === 'True',
        chargeImmediately: formValues.chargeTimeline === COLLECTION_OPTIONS.NOW,
        addToNextInvoice: isAddToNextInvoice,
        dueDate: formValues.dueDate?.toISOString() || '',
        invoiceId: newInvoiceId,
        invoiceItems: formValues.invoiceItems.map(item => ({
          invoiceItemId: item.invoiceItemId,
          productPrice: item.productPrice,
          quantity: item.quantity,
          itemPrice: Math.round(item.itemPrice * 100),
          taxRateValue: item.taxRateValue / 100,
          discount: item.discount ?? null,
        })),
        paymentMethod:
          formValues.isTapToPay || formValues.nonStripePaymentMethod ? null : formValues.paymentMethod,
        nonStripePaymentMethod: formValues.isTapToPay ? null : formValues.nonStripePaymentMethod,
        isTapToPay: formValues.isTapToPay,
        isSaveCardForFuturePayments: formValues.isSaveCardForFuturePayments,
      },
      {
        onSuccess: res => {
          if (formValues.isTapToPay) {
            setInvoiceId(res.data.invoiceId);
            setIsTapToPayDrawerOpened(true);
            const isChargeNow = formValues.chargeTimeline === COLLECTION_OPTIONS.NOW;
            if (isChargeNow) {
              staffTracking.trackTapToPayCollectionStarted({
                page: tapToPayPage,
                invoice_id: res.data.invoiceId,
                athlete_id: formValues.athlete.athleteId,
                amount: total,
                item_count: formValues.invoiceItems.length,
                save_card: formValues.isSaveCardForFuturePayments,
              });
            } else {
              staffTracking.trackTapToPayCardSaveStarted({
                // card_save_started only accepts 'retails' | 'athlete_package'
                // per spec; 'misc_and_fees' has no future-date path so narrow here.
                page: tapToPayPage === 'misc_and_fees' ? 'retails' : tapToPayPage,
                invoice_id: res.data.invoiceId,
                athlete_id: formValues.athlete.athleteId,
                billing_start_date: formValues.dueDate?.toISOString() ?? '',
              });
            }
            return;
          }

          if (isAddToNextInvoice) {
            setInvoiceId(res.data.invoiceId);
            setSuccessModalOpened(true);
          } else {
            successNotification(`Invoice creation succeeded`, `Status is ${res.data.status}`);
          }

          if (res.data.athlete?.athleteId) {
            staffTracking.trackProductSalePurchased(
              res.data.invoiceId,
              res.data.athlete.athleteId,
              res.data.athlete.fullName || res.data.athlete.athleteId,
              formValues.invoiceItems.length,
              formValues.nonStripePaymentMethod || formValues.paymentMethod || 'Unknown',
              total,
              productTypeModule,
            );
          }

          handleClearCart();
          form.reset();
        },
        onError: (error: any) => {
          errorNotification(
            formValues.chargeTimeline === COLLECTION_OPTIONS.NOW
              ? 'Charge failed'
              : 'Invoice creation failed',
            error.response?.data.message,
          );
        },
      },
    );
  };

  const handleCloseTapToPay = (): void => {
    setIsTapToPayDrawerOpened(false);
    setInvoiceId(null);
    handleClearCart();
    form.reset();
  };

  const handleChangePaymentMethod = (): void => {
    setIsTapToPayDrawerOpened(false);
    setInvoiceId(null);
    form.setFieldValue('paymentMethod', '');
    form.setFieldValue('nonStripePaymentMethod', null);
    form.setFieldValue('isTapToPay', false);
    form.setFieldValue('isSaveCardForFuturePayments', false);
  };

  const handleSuccessModalClose = (): void => {
    setSuccessModalOpened(false);
    setInvoiceId(null);
    setSelectedAthleteId(null);
    handleClearCart();
  };

  const handleGoToInvoice = (): void => {
    if (selectedAthleteId && invoiceId) {
      navigate(`/users/athletes/${selectedAthleteId}/invoices?invoiceId=${invoiceId}`);
    }
    handleSuccessModalClose();
  };

  useEffect(() => {
    const invoiceItems: InvoiceItem[] = [];
    form.values.invoiceItems.forEach(invoiceItem => {
      if (cartItems.find(cartItem => invoiceItem.productId == cartItem.productId)) {
        invoiceItems.push(invoiceItem);
      }
    });
    cartItems.forEach(cartItem => {
      if (!!!form.values.invoiceItems.find(invoiceItem => invoiceItem.productId == cartItem.productId)) {
        invoiceItems.push(getInvoiceItemFromProduct(cartItem));
      }
    });
    form.setFieldValue('invoiceItems', invoiceItems);
    if (cartItems.length === 0) {
      form.reset();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems]);

  useEffect(() => {
    if (
      form.values.chargeTimeline === COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE &&
      !isLoadingUpcomingInvoices &&
      !hasUpcomingInvoices
    ) {
      form.setFieldValue('chargeTimeline', COLLECTION_OPTIONS.NOW);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUpcomingInvoices]);

  return (
    <Stack
      className={classes.container}
      pt='md'
      pb='xl'
      style={{ display: visible ? undefined : 'none', height: '100%' }}
    >
      <Group position='apart' align='end' px='lg' noWrap>
        <Group noWrap>
          {!isDesktop && (
            <ActionIcon size='lg' radius='md' onClick={closeCart}>
              <IconChevronLeft color={theme.black} />
            </ActionIcon>
          )}
          <Title order={2}>Shopping Cart</Title>
        </Group>
        <Button
          color={form.values.chargeTimeline === COLLECTION_OPTIONS.NOW ? 'kiloBlue' : 'green'}
          form='shopping-cart'
          type='submit'
          loading={createInvoiceMutation.isLoading}
          disabled={!permission || isChargeButtonDisabled}
          data-cy='chargeRetailButton'
        >
          {getSubmitButtonText(form.values.chargeTimeline)}
        </Button>
      </Group>

      <Divider />

      <form id='shopping-cart' onSubmit={form.onSubmit(handleSubmit)} style={{ overflow: 'scroll' }}>
        <Stack spacing='md' px='xl'>
          <div className={classes.top}>
            <AthleteAutoComplete
              {...form.getInputProps('athlete')}
              className={classes.athlete}
              label='Athlete'
              placeholder='Select an athlete'
              icon={<IconUser size={theme.fontSizes.lg} />}
              onChange={handleAthleteChange}
              required
              data-cy='athleteSearchRetail'
              disabled={!permission}
            />
            <Select
              {...form.getInputProps('chargeTimeline')}
              className={classes.collection}
              label='Collection Date'
              data={availableCollectionOptions}
              onChange={handleChargeTimelineChange}
              required
              disabled={!permission || isLoadingUpcomingInvoices}
              rightSection={isLoadingUpcomingInvoices ? <Loader size='xs' /> : undefined}
            />
            <DateInput
              {...form.getInputProps('dueDate')}
              className={classes.date}
              label=' '
              minDate={minDueDate}
              required={form.values.chargeTimeline === COLLECTION_OPTIONS.FUTURE_DATE}
              withAsterisk={false}
              sx={{
                visibility:
                  form.values.chargeTimeline === COLLECTION_OPTIONS.FUTURE_DATE ? 'visible' : 'hidden',
              }}
              disabled={!permission}
            />
            <Stack>
              <PaymentMethodSelect
                className={classes.payment}
                label='Payment Method'
                placeholder='Select Payment Method'
                athleteId={form.values.athlete.athleteId}
                value={form.values.paymentMethod}
                onChange={(value: string) => handlePaymentChange(value)}
                allowCash
                allowTapToPay={!!gymData?.data.stripeTerminalReader}
                useDefaultPayment
                data-cy='paymentMethodRetailInput'
                isLoading={isGymDataLoading}
                disabled={
                  !permission ||
                  form.values.chargeTimeline === COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE ||
                  isGymDataError
                }
              />

              {form.values.isTapToPay && (
                <StripeTerminalReaderStatusIndicator enabled={!!gymData?.data.stripeTerminalReader} />
              )}

              {form.values.isTapToPay && form.values.chargeTimeline === COLLECTION_OPTIONS.NOW && (
                <Checkbox
                  label='Save card for future payments'
                  {...form.getInputProps('isSaveCardForFuturePayments')}
                />
              )}
            </Stack>

            <Select
              {...form.getInputProps('autoPay')}
              className={classes.autopay}
              label='AutoPay'
              data={[
                { value: 'True', label: 'On' },
                { value: 'False', label: 'Off' },
              ]}
              disabled={form.values.nonStripePaymentMethod !== null || !permission}
              sx={{
                visibility:
                  form.values.chargeTimeline == COLLECTION_OPTIONS.FUTURE_DATE ? 'visible' : 'hidden',
              }}
              required
            />
          </div>

          <Divider />

          <Group position='apart' align='end' noWrap>
            <Title order={4}>Items in Cart</Title>
            <Button
              size='xs'
              color='red'
              disabled={!permission || createInvoiceMutation.isLoading}
              onClick={handleClearCart}
              data-cy='retailClearCartButton'
            >
              Clear Cart
            </Button>
          </Group>

          {form.values.invoiceItems.length > 0 && (
            <>
              <div className={classes.items}>
                <div className={classes.image} />
                <div className={classes.name}></div>
                <div className={classes.pretaxPrice}>
                  <Input.Label>{copy.components.MoneyField.priceInputLabel}</Input.Label>
                </div>
                <div className={classes.quantity}>
                  <Input.Label>Quantity</Input.Label>
                </div>
                <div className={classes.taxRate}>
                  <Input.Label>{copy.financial.taxRate}</Input.Label>
                </div>
                <div className={classes.trash} />
              </div>

              <Divider />

              {form.values.invoiceItems.map((ii, i) => (
                <React.Fragment key={ii.productName}>
                  <div className={classes.items}>
                    <div className={classes.image}>
                      <Avatar src={ii.image} sx={{ boxShadow: theme.shadows.md }}>
                        {ii.productName[0]}
                      </Avatar>
                    </div>
                    <div className={classes.name} style={{ alignSelf: 'center' }}>
                      <Text truncate fw={600} data-cy='retailCartItems'>
                        {ii.productName}
                      </Text>
                      <AvailableDiscounts
                        athleteId={form.values.athlete.athleteId || undefined}
                        selectedDiscountId={ii.discount}
                        form={form}
                        fieldPath={`invoiceItems.${i}.discount`}
                        productId={ii.productId}
                      />
                    </div>
                    <div className={classes.pretaxPrice}>
                      <MoneyField
                        country={country}
                        {...form.getInputProps(`invoiceItems.${i}.itemPrice`)}
                        placeholder={copy.components.MoneyField.priceInputPlaceholder}
                        min={0}
                        required
                        inputMode='text'
                      />
                    </div>
                    <div className={classes.quantity}>
                      <NumberInput
                        {...form.getInputProps(`invoiceItems.${i}.quantity`)}
                        placeholder='Set quantity'
                        min={1}
                        step={1}
                        required
                      />
                    </div>
                    <div className={classes.taxRate}>
                      <TaxRatePercentageInput
                        {...form.getInputProps(`invoiceItems.${i}.taxRateValue`)}
                        placeholder='Set tax rate'
                        required
                        inputMode='text'
                      />
                    </div>
                    <div className={classes.trash}>
                      <ActionIcon size='lg' color='red' onClick={() => handleTrashIcon(i)}>
                        <IconTrash />
                      </ActionIcon>
                    </div>
                  </div>
                  <Divider />
                </React.Fragment>
              ))}
            </>
          )}

          <InvoiceSummary athleteName={form.values.athlete.label} invoiceItems={form.values.invoiceItems} />
        </Stack>
      </form>

      <AddToInvoiceSuccessModal
        opened={successModalOpened}
        onClose={handleSuccessModalClose}
        productTypeModule={productTypeModule}
        onGoToInvoice={handleGoToInvoice}
      />

      {invoiceId && (
        <TapToPayModal
          opened={isTapToPayDrawerOpened}
          invoiceId={invoiceId}
          isChargeNow={form.values.chargeTimeline === COLLECTION_OPTIONS.NOW}
          onClose={handleCloseTapToPay}
          onChangePaymentMethod={handleChangePaymentMethod}
          trackingContext={{
            page: tapToPayPage,
            athleteId: form.values.athlete.athleteId,
            amount: total,
            itemCount: form.values.invoiceItems.length,
            saveCard: form.values.isSaveCardForFuturePayments,
            billingStartDate: form.values.dueDate?.toISOString(),
          }}
        />
      )}
    </Stack>
  );
};

export default ShoppingCart;
